//----- Component --------------------------------------------------------/
// Developer     : Guillermo Sedran
// Creation Date : 26/03/2025
// Version       : 1
// Description   : Vista previa de los datos normalizados listos para guardar
//
//------Changes --------------------------------------------------------------------- -------------//
// Change Date   :
// Change by     :
// Change description:
//
// Change Date   :
// Change by     :
// Change description:
//
//========================================================================/

import React, { useState, useEffect } from "react";
import { UrlApi } from "../../services/apirest";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Backdrop,
  CircularProgress,
  DialogContent,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { v4 as uuidv4 } from "uuid";

import { Snackbar, Alert } from "@mui/material";
import { LoadingButton } from "@mui/lab";

import CargaProductosEnReporte from "./CargaProductosEnReporte";

// ICONOS
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";

const CargarDatos2 = ({
  data,
  handlePreviousStep,
  proveedorSeleccionado,
  onDatosGuardados,
}) => {
  // Generar UUID único para toda la transacción
  const uuidTransaccion = React.useMemo(() => uuidv4(), []);

  // Estados para feedback de la operación
  const [loading, setLoading] = useState(false);

  // *************************************************************************

  const mapping = {
    fecha: "FECHA",
    patente: "PATENTE",
    producto: "PRODUCTO",
    cantidad: "CANTIDAD",
    nro_comprobante: "N° COMPROBANTE",
  };

  const columns = Object.keys(mapping).map((key) => ({
    field: key, // Uso la key estandarizada en el objeto normalizado
    headerName: mapping[key], // El header a mostrar
    width: 200,
    valueGetter: (params) => (params.row[key] != null ? params.row[key] : "-"), // Se muestra 0 o "-" solo cuando el valor es null, undefined o una cadena vacía ("")
  }));

  // **************************************************************

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // **************************************************************

  // Para el flujo de “productos faltantes”
  const [productosFaltantes, setProductosFaltantes] = useState([]);
  const [showModalProductos, setShowModalProductos] = useState(false);

  // **************************************************************

  // Función para guardar los datos
  const handleGuardarFinal = async () => {
    setLoading(true);
    try {
      // ——— Filtrar filas válidas ———
      const datosValidos = data.filter((row) => {
        const comp = String(row.nro_comprobante || "").trim();
        return comp !== "" && comp !== "-";
      });
      if (datosValidos.length === 0) {
        throw { tipo: "warning", msg: "No hay datos válidos para guardar." };
      }

      // ——— Datos con proveedor, uuid y fecha ———
      const datosParaGuardar = datosValidos.map((row) => ({
        ...row,
        proveedor: proveedorSeleccionado,
        uuid_transaccion: uuidTransaccion,
        fecha: row.fecha ? row.fecha.replace("T", " ").substring(0, 19) : null,
      }));

      // ——— Detectar productos faltantes ———
      const nombresEnBatch = [
        ...new Set(datosParaGuardar.map((d) => d.producto.trim())),
      ];
      const resProd = await fetch(`${UrlApi}/productos_reporte`);
      const productosExistentes = await resProd.json();
      const nombresExistentes = new Set(
        productosExistentes.map((p) => p.nombre.trim())
      );
      const faltantes = nombresEnBatch.filter((n) => !nombresExistentes.has(n));

      if (faltantes.length > 0) {
        // Hay que mapear unidades antes de continuar
        setProductosFaltantes(faltantes);
        setShowModalProductos(true);
        throw { tipo: "pause" }; // Interrumpo el flujo sin mostrar error cuando hay productos faltantes
      }

      // ——— Si no hay faltantes, seguimos y hacemos el bulk‐insert del reporte ———
      await saveReporte(datosParaGuardar);
    } catch (err) {
      // Controlamos errores y pausas
      if (err.tipo === "warning") {
        setSnackbarSeverity("warning");
        setSnackbarMessage(err.msg);
        setSnackbarOpen(true);
      }
      // err.tipo === "pause" para parar el flujo, no muestro snackbar
    } finally {
      setLoading(false);
    }
  };

  // Se guarda el reporte una vez gaurdados los productos faltantes
  const saveReporte = async (datosParaGuardar) => {
    const res = await fetch(`${UrlApi}/vehiculos_reporte_combustibles`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(datosParaGuardar),
    });
    if (!res.ok) throw new Error("Error al guardar reporte");
    const result = await res.json();
    setSnackbarSeverity(
      result.message.includes("Carga realizada") ? "success" : "error"
    );
    setSnackbarMessage(result.message);
    setSnackbarOpen(true);
    if (result.message.includes("Carga realizada")) {
      onDatosGuardados(); // refrescar tabla padre
    }
  };

  // Cuando el modal confirma el bulk de productos
  const handleConfirmarProductos = async (nuevosProductos) => {
    setLoading(true);
    try {
      // Bulk‐POST a /productos_reporte/bulk
      const res = await fetch(`${UrlApi}/productos_reporte/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(nuevosProductos),
      });
      if (!res.ok) throw new Error("Error al crear productos");
      // Una vez creados los productos, se cierra el modal y reintento el guardar del reporte
      setShowModalProductos(false);
      await handleGuardarFinal();
    } catch (e) {
      console.error(e);
      setSnackbarSeverity("error");
      setSnackbarMessage("Error al crear productos necesarios.");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogContent sx={{ p: 2 }}>
        <Box sx={{ height: "60vh", width: "100%" }}>
          <DataGrid
            rows={data}
            columns={columns}
            rowHeight={25}
            density="compact"
            rowSelection={false}
            disableSelectionOnClick
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            disableSelectionOnClickdeFooter
            disableColumnSorting
            disableRowSelectionOnClick
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
              },
              "& .MuiDataGrid-cell": {
                fontSize: "0.75rem",
                padding: "4px 8px", // Deshabilita el efecto de focus
                "&:focus": {
                  outline: "none", // Elimina el borde de enfoque
                },
                // Deshabilita el cursor de selección
                cursor: "default",
              },
              // Elimina el efecto hover
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "inherit !important",
              },
              // Estilos para filas
              "& .MuiDataGrid-row.even": {
                backgroundColor: "#white",
              },
              "& .MuiDataGrid-row.odd": {
                backgroundColor: "#f5f5f5",
              },
              // Mantener color de las filas al pasar por encima
              "& .MuiDataGrid-row.even:hover": {
                backgroundColor: "#f5f5f5 !important",
              },
              "& .MuiDataGrid-row.odd:hover": {
                backgroundColor: "white !important",
              },
            }}
            slots={{
              noRowsOverlay: () => null, // Eliminar componente overlay
            }}
            slotProps={{
              row: {
                tabIndex: -1, // Deshabilita navegación por teclado
              },
            }}
          />
        </Box>

        <Grid container sx={{ mt: 2 }}>
          <Grid item xs={6} sx={{ textAlign: "left" }}>
            <Button
              variant="contained"
              size="small"
              color="error"
              sx={{ minWidth: 100 }}
              startIcon={<NavigateBeforeIcon />}
              onClick={handlePreviousStep}
            >
              Volver
            </Button>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: "right" }}>
            <LoadingButton
              onClick={handleGuardarFinal}
              loading={loading}
              size="small"
              variant="contained"
              color="primary"
            >
              Guardar Reporte
            </LoadingButton>
          </Grid>
        </Grid>
      </DialogContent>

      <CargaProductosEnReporte
        open={showModalProductos}
        productos={productosFaltantes}
        onCancel={() => setShowModalProductos(false)}
        onConfirm={handleConfirmarProductos}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Loader */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default CargarDatos2;
